import React from "react";
import styled from "styled-components";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { DropdownContent } from "@sonder/ui/source/styled/components/DropdownContent";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import {
  CircleButton,
  CircleButtonProps,
} from "@sonder/ui/source/styled/fragments/CircleButton";
import { useSegment } from "analytics/segment";
import { useErrorReporterFiltered } from "core/hooks/useErrorReporterFiltered";
import { useUpdateTask } from "core/queries/UpdateTask";
import { normalizeType } from "tasks/helpers";
import { FilterStatus, TypeEnum } from "typings/globalGQLTypes";

const AdditionalOptionsMenuDropdown = styled(Dropdown)`
  flex-grow: 0;
`;

const AdditionalOptionsMenuDropdownContent = styled(DropdownContent)`
  flex-direction: column;
  left: 0.5rem;
  min-width: 18rem;
`;

const AdditionalOptionsMenuDropdownOption = styled(Button)`
  align-items: center;
  justify-content: flex-start;
`;

const isDisabled = (taskType: string, taskStatus: string | null) => {
  const normalizedTaskType = taskType && normalizeType(taskType);
  // Conditions for Disabled:
  switch (normalizedTaskType) {
    // Cleans and Space Cleans cannot be Archived when in Progress.
    case TypeEnum.Tom_Tasks_SpaceClean:
    case TypeEnum.Tom_Tasks_Clean:
      return taskStatus !== FilterStatus.ready;
    default:
      return false;
  }
};

interface AdditionalOptionsMenuProps {
  className?: string;
  eventName: string;
  loading?: boolean;
  onClose?(): void;
  onOpen?(): void;
  taskId: string;
  taskType: string;
  status: string | null;
  renderTrigger: () => JSX.Element;
}

interface AdditionalOptionsMenuTriggerProps extends CircleButtonProps {}

export const AdditionalOptionsMenuTrigger = ({
  backgroundVariant,
  ...rest
}: AdditionalOptionsMenuTriggerProps) => (
  <CircleButton
    backgroundVariant={backgroundVariant}
    data-testid="additional-options-button"
    {...rest}
  >
    <MoreHorizIcon />
  </CircleButton>
);

export const AdditionalOptionsMenu = ({
  className,
  eventName,
  loading,
  onClose = () => undefined,
  onOpen = () => undefined,
  renderTrigger,
  taskId,
  taskType,
  status,
}: AdditionalOptionsMenuProps) => {
  const { track } = useSegment();
  const errorReporter = useErrorReporterFiltered();
  const [onUpdateTask] = useUpdateTask({
    refetchQueries: ["GetTasks", "GetTask"],
  });
  const disabled = isDisabled(taskType, status);
  return (
    <AdditionalOptionsMenuDropdown
      className={className}
      trigger={renderTrigger && renderTrigger()}
      onClose={onClose}
      onOpen={onOpen}
    >
      {({ isOpen, closePortal }) =>
        isOpen && (
          <AdditionalOptionsMenuDropdownContent align="right">
            <AdditionalOptionsMenuDropdownOption
              noBorder
              disabled={loading || disabled}
              onClick={async () => {
                const nextStatus =
                  status === FilterStatus.canceled
                    ? FilterStatus.ready
                    : FilterStatus.canceled;
                closePortal();
                try {
                  await onUpdateTask({
                    variables: { taskId, status: nextStatus },
                  });
                  track({
                    eventName,
                    action: "click",
                    element: "cancel_task_button",
                    props: { nextValue: nextStatus },
                  });
                } catch (error) {
                  errorReporter.notify(error);
                }
              }}
            >
              {status === FilterStatus.canceled
                ? "Unarchive Task"
                : "Archive Task"}
            </AdditionalOptionsMenuDropdownOption>
          </AdditionalOptionsMenuDropdownContent>
        )
      }
    </AdditionalOptionsMenuDropdown>
  );
};
